/**
 * ============================================================================
 * 📡 Backend API Service Connector (with Vite proxy support)
 * ============================================================================
 */

export class ApiService {
  // Defaults to relative path (handled by Vite's dev proxy, or same-origin
  // in prod behind nginx). Set VITE_API_BASE_URL (in frontend/.env.local) to
  // point this dev server at a backend running on a different machine —
  // e.g. a VPS collecting data 24/7 — so UI work here never touches it.
  constructor(baseUrl = import.meta.env.VITE_API_BASE_URL || '') {
    this.baseUrl = baseUrl;
  }

  async getLiveSnapshot(filters = {}) {
    const params = new URLSearchParams();
    if (filters.minAlt) params.append('min_alt', filters.minAlt);
    if (filters.maxAlt) params.append('max_alt', filters.maxAlt);
    if (filters.airline) params.append('airline', filters.airline);
    if (filters.inAirOnly) params.append('in_air_only', 'true');

    const url = `${this.baseUrl}/api/live${params.toString() ? '?' + params.toString() : ''}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  async getStats() {
    const res = await fetch(`${this.baseUrl}/api/stats`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  async getPlayback(stepSeconds = 5) {
    const res = await fetch(`${this.baseUrl}/api/playback?step_seconds=${stepSeconds}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  async getTrajectory(registration, limit = 500) {
    const res = await fetch(`${this.baseUrl}/api/aircraft/${encodeURIComponent(registration)}/trajectory?limit=${limit}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  async getAircraftProfile(registration) {
    const res = await fetch(`${this.baseUrl}/api/aircraft/${encodeURIComponent(registration)}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  getPhotoUrl(registration) {
    return `${this.baseUrl}/api/photos/${encodeURIComponent(registration)}`;
  }

  // --------------------------------------------------------------------------
  // 📋 Completed Flights & Maritime Voyages (Ephemeral Lifecycle Summaries)
  // --------------------------------------------------------------------------
  async getCompletedFlights({ limit = 50, airline = '' } = {}) {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit);
    if (airline) params.append('airline', airline);
    const url = `${this.baseUrl}/api/flights/completed${params.toString() ? '?' + params.toString() : ''}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  async getCompletedFlightsStats() {
    const res = await fetch(`${this.baseUrl}/api/flights/completed/stats`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  async getCompletedVoyages({ limit = 50 } = {}) {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit);
    const url = `${this.baseUrl}/api/voyages/completed${params.toString() ? '?' + params.toString() : ''}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  async getLiveFlights() {
    const res = await fetch(`${this.baseUrl}/api/flights/live`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  // --------------------------------------------------------------------------
  // 🚢 Marine AIS Radar API Methods
  // --------------------------------------------------------------------------
  async getMarineLive(filters = {}) {
    const params = new URLSearchParams();
    if (filters.minSog != null) params.append('min_sog', filters.minSog);
    if (filters.shipType) params.append('ship_type', filters.shipType);

    const url = `${this.baseUrl}/api/marine/live${params.toString() ? '?' + params.toString() : ''}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  async getMarineDetail(mmsi) {
    const res = await fetch(`${this.baseUrl}/api/marine/${encodeURIComponent(mmsi)}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  getMarinePhotoUrl(mmsi) {
    return `${this.baseUrl}/api/marine/photos/${encodeURIComponent(mmsi)}`;
  }

  async getCombinedRadar(mode = 'all', { bounds = null, zoom = null, zone = null } = {}) {
    let url = `${this.baseUrl}/api/radar/combined?mode=${encodeURIComponent(mode)}`;
    if (bounds) url += `&bounds=${encodeURIComponent(bounds)}`;
    if (zoom != null) url += `&zoom=${encodeURIComponent(zoom)}`;
    if (zone) url += `&zone=${encodeURIComponent(zone)}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  /**
   * Triggers the backend to download and permanently save the rest of an
   * aircraft's photo gallery (it only keeps 1 eager photo per airframe by
   * default). Meant to be called only when a user actually selects this
   * aircraft — safe to call repeatedly, the backend no-ops once enough
   * photos are already cached.
   */
  async fetchMorePhotos(registration) {
    const res = await fetch(`${this.baseUrl}/api/aircraft/${encodeURIComponent(registration)}/photos/fetch-more`, {
      method: 'POST'
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  // --------------------------------------------------------------------------
  // 🧭 Strategic Infrastructure POIs (Airports & Seaports)
  // --------------------------------------------------------------------------
  async getAirports(type = null) {
    const url = type 
      ? `${this.baseUrl}/api/infrastructure/airports?type=${encodeURIComponent(type)}`
      : `${this.baseUrl}/api/infrastructure/airports`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  async getAirportDetail(code) {
    const res = await fetch(`${this.baseUrl}/api/infrastructure/airports/${encodeURIComponent(code)}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  async getSeaports(type = null) {
    const url = type 
      ? `${this.baseUrl}/api/infrastructure/seaports?type=${encodeURIComponent(type)}`
      : `${this.baseUrl}/api/infrastructure/seaports`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  async getSeaportDetail(code) {
    const res = await fetch(`${this.baseUrl}/api/infrastructure/seaports/${encodeURIComponent(code)}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  async getAllInfrastructure() {
    const res = await fetch(`${this.baseUrl}/api/infrastructure/all`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  // --------------------------------------------------------------------------
  // 🛣️ Flight Corridors & Maritime Shipping Lanes
  // --------------------------------------------------------------------------
  async getAirways(type = null) {
    const url = type 
      ? `${this.baseUrl}/api/corridors/airways?type=${encodeURIComponent(type)}`
      : `${this.baseUrl}/api/corridors/airways`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  async getShippingLanes(type = null) {
    const url = type 
      ? `${this.baseUrl}/api/corridors/shipping-lanes?type=${encodeURIComponent(type)}`
      : `${this.baseUrl}/api/corridors/shipping-lanes`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  async getAllCorridors() {
    const res = await fetch(`${this.baseUrl}/api/corridors/all`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }


  // --------------------------------------------------------------------------
  // 📡 Airspace Structure, Bangkok FIR & ATC Radar Control Sectors
  // --------------------------------------------------------------------------
  async getFIRBoundary() {
    const res = await fetch(`${this.baseUrl}/api/airspace/fir-boundary`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  async getATCSectors() {
    const res = await fetch(`${this.baseUrl}/api/airspace/sectors`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  async getWaypoints() {
    const res = await fetch(`${this.baseUrl}/api/airspace/waypoints`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  async getAllAirspace() {
    const res = await fetch(`${this.baseUrl}/api/airspace/all`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  // --------------------------------------------------------------------------
  // 📊 Real-Time Traffic Density Analytics
  // --------------------------------------------------------------------------
  async getTrafficDensity() {
    const res = await fetch(`${this.baseUrl}/api/analytics/density`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  // --------------------------------------------------------------------------
  // 🌤️ Live Weather, METAR & RainViewer Real-time Rain Radar
  // --------------------------------------------------------------------------
  async getWeatherStations() {
    const res = await fetch(`${this.baseUrl}/api/weather/stations`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  async getWeatherRadar() {
    const res = await fetch(`${this.baseUrl}/api/weather/radar`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  async getAllWeather() {
    const res = await fetch(`${this.baseUrl}/api/weather/all`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  // --------------------------------------------------------------------------
  // 📈 Dead Reckoning Trajectory Velocity Leader Projections
  // --------------------------------------------------------------------------
  async getProjections() {
    const res = await fetch(`${this.baseUrl}/api/radar/projections`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  async getTargetProjection(targetId) {
    const res = await fetch(`${this.baseUrl}/api/radar/projections/${encodeURIComponent(targetId)}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  // --------------------------------------------------------------------------
  // ⚡ Tactical Geo-Fencing & Strategic Restricted Zones
  // --------------------------------------------------------------------------
  async getGeofencingZones() {
    const res = await fetch(`${this.baseUrl}/api/geofencing/zones`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  async getGeofencingStatus() {
    const res = await fetch(`${this.baseUrl}/api/geofencing/status`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  // --------------------------------------------------------------------------
  // 🎯 Tactical Radar Range Rings & Crosshairs
  // --------------------------------------------------------------------------
  async getRangeRingsCenters() {
    const res = await fetch(`${this.baseUrl}/api/radar/range-rings/centers`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  async getRangeRingsConfig(centerId = 'VTBS') {
    const res = await fetch(`${this.baseUrl}/api/radar/range-rings/config?center_id=${encodeURIComponent(centerId)}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  // --------------------------------------------------------------------------
  // ⚓ Strategic Marine Anchorage & Mooring Zones
  // --------------------------------------------------------------------------
  async getAnchorageZones() {
    const res = await fetch(`${this.baseUrl}/api/maritime/anchorages`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  async getAnchorageStatus() {
    const res = await fetch(`${this.baseUrl}/api/maritime/anchorages/status`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  // --------------------------------------------------------------------------
  // 🎯 Dynamic Range & Bearing Geodesic Measurement Tool
  // --------------------------------------------------------------------------
  async calculateGeodesic(startLat, startLon, endLat, endLon, speedKts = 0) {
    const params = new URLSearchParams({
      start_lat: startLat,
      start_lon: startLon,
      end_lat: endLat,
      end_lon: endLon,
      speed_kts: speedKts
    });
    const res = await fetch(`${this.baseUrl}/api/tools/geodesic/calculate?${params.toString()}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  // --------------------------------------------------------------------------
  // 🌊 Real-Time Oceanographic Data & Sea State Observation (Open-Meteo)
  // --------------------------------------------------------------------------
  async getAllOceanZones() {
    const res = await fetch(`${this.baseUrl}/api/ocean/all`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  async getOceanZone(zoneId) {
    const res = await fetch(`${this.baseUrl}/api/ocean/${encodeURIComponent(zoneId)}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }
}



